import { ChevronRight, Lock } from "lucide-react";
import { pillarOverview, benchmarkPillars, noWebsiteInfo } from "./summaryModel";
import { PillarRadar } from "./primitives/PillarRadar";
import { PILLAR_MAX, scoreTone, lockedScrollTarget } from "./reportUtils";
import { S } from "./summaryCopy";
import { T } from "./i18n";

// Six area bars, each with an honest "N points in the report" badge. Only counts:
// which points are locked is the paid report's job.
const goLocked = () => lockedScrollTarget()?.scrollIntoView({ behavior: "smooth", block: "start" });

export default function PillarOverview({ data, lang }) {
  const allRows = pillarOverview(data);
  if (!allRows.length) return null;
  // Without a website, web/onpage/local at 0 are not weaknesses but "not
  // measurable": one explanatory line instead of three empty rows.
  const noSite = noWebsiteInfo(data);
  const zeroed = noSite ? noSite.zeroed : [];
  const rows = allRows.filter((p) => !zeroed.includes(p.key));
  const C = { ...(S[lang] || S.de).pillars, noSite: (S[lang] || S.de).noSite };
  const labels = (T[lang] || T.de).pillars;
  const bench = benchmarkPillars(data.top3, Object.keys(labels));
  return (
    <section data-testid="pillar-overview" className="mt-6 rounded-2xl border bg-card p-5 shadow-sm sm:p-6">
      <h2 className="mb-1.5 font-heading text-lg font-semibold sm:text-xl">{C.title}</h2>
      <p className="mb-4 text-base text-muted-foreground">{C.desc}</p>
      {/* Same radar as the paid report; only numbers. Short labels keep it inside the card at phone width. */}
      <div className="mb-4" data-testid="pillar-radar">
        <PillarRadar
          pillars={data.score_pillars}
          pillarMax={PILLAR_MAX}
          pillarKeys={Object.keys(labels)}
          pillarLabels={C.radarLabels}
          benchmarkPillars={bench}
          size={320}
          padX={75}
          padY={24}
          radiusFactor={0.85}
          labelSize={13}
          wrapLabels
          title={C.radarTitle}
        />
        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded-sm border" style={{ backgroundColor: "hsla(217,100%,59%,0.22)", borderColor: "hsl(217,100%,59%)" }} aria-hidden="true" />
            {C.radarYou}
          </span>
          {bench && (
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-5 border-t-2 border-dashed" style={{ borderColor: "hsl(215,16%,55%)" }} aria-hidden="true" />
              {C.radarBenchmark}
            </span>
          )}
        </div>
        {bench && <p className="mt-1.5 text-center text-xs text-muted-foreground">{C.radarBenchmarkNote}</p>}
      </div>
      <ul className="divide-y">
        {zeroed.length > 0 && (
          <li className="py-3" data-testid="pillar-no-site">
            <span className="text-base font-medium">{zeroed.map((k) => labels[k]).join(", ")}</span>
            <p className="mt-1 text-sm text-muted-foreground">{C.noSite.unmeasurable}</p>
          </li>
        )}
        {rows.map((p) => {
          const tone = scoreTone(p.pct);
          const body = (
            <>
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-base font-medium">{labels[p.key]}</span>
                <span className="flex items-center gap-1">
                  <span className="text-sm font-semibold tabular-nums" style={{ color: tone.fg }}>{`${p.score}/${p.max}`}</span>
                  {p.state === "locked" && <ChevronRight className="h-4 w-4 text-muted-foreground print:hidden" aria-hidden="true" />}
                </span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full" style={{ width: `${p.pct}%`, backgroundColor: tone.bar }} />
              </div>
              {p.manualProgress && p.manualProgress.reviewed < p.manualProgress.total && (
                <p className="mt-1.5 text-xs text-muted-foreground" data-testid="gbp-manual-progress">
                  {C.manualReviewed(p.manualProgress.reviewed, p.manualProgress.total)}
                </p>
              )}
              {p.state !== "open" && (
                <div className="mt-2 text-sm text-muted-foreground">
                  {p.state === "locked" && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border bg-secondary px-2.5 py-0.5">
                      <Lock className="h-3.5 w-3.5" /> {C.locked(p.lockedCount)}
                    </span>
                  )}
                  {p.state === "sample" && <span>{C.sampleOpen(1)}</span>}
                  {p.state === "complete" && (
                    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 font-medium text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">{`✓ ${C.complete}`}</span>
                      <a href="#angebot" className="underline-offset-2 hover:underline">{C.completeHint}</a>
                    </span>
                  )}
                </div>
              )}
            </>
          );
          // Only locked / sample rows have somewhere to scroll to; the rest are plain rows.
          const clickable = p.state === "locked" || p.state === "sample";
          return (
            <li key={p.key}>
              {clickable ? (
                <button
                  type="button"
                  onClick={goLocked}
                  className="block w-full rounded-md py-3 text-left transition hover:bg-secondary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring print:hover:bg-transparent"
                >
                  {body}
                </button>
              ) : (
                <div className="py-3">{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
